import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Box,
  Divider,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HistoryIcon from '@mui/icons-material/History';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ReplayIcon from '@mui/icons-material/Replay';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import withShipment from '../../../withShipment';
import {
  listLicensePayments,
  updateLicensePayment,
  recordNextCyclePayment,
} from '../../../redux/actions/settings';
import {
  licensePaymentsSelector,
  licensePaymentsTotalSelector,
} from '../../../redux/selectors/account';
import { PAYMENT_METHODS, PAYMENT_METHOD_LIST } from './wizardConfig';

// Collection status values — must match the backend allowed transitions
// in CompanyService.updateLicensePayment.
const COLLECTION_STATUS = {
  PENDING: 'pending',
  COLLECTED: 'collected',
  UNCOLLECTIBLE: 'uncollectible',
};

// Schema describing which payment-detail fields belong to each method.
// Mirrors CompanyWizardV3's Step 4 without importing wizard internals so
// the dialog stays self-contained. `autoFocus` picks the first field.
const DETAIL_FIELDS_BY_METHOD = {
  [PAYMENT_METHODS.CASH_ZELLE]: [
    { key: 'senderName', label: 'Sender Name' },
    { key: 'amountSent', label: 'Amount Sent' },
    { key: 'zelleHandle', label: 'Zelle Handle / Phone' },
    { key: 'confirmation', label: 'Confirmation #' },
  ],
  [PAYMENT_METHODS.CARD]: [
    { key: 'cardNumber', label: 'Card Number' },
    { key: 'expiry', label: 'Expiry (MM/YY)' },
    { key: 'cvc', label: 'CVC' },
    { key: 'cardName', label: 'Name on Card' },
  ],
  [PAYMENT_METHODS.BANK]: [
    { key: 'routing', label: 'Routing Number' },
    { key: 'account', label: 'Account Number' },
  ],
  [PAYMENT_METHODS.PENDING]: [
    { key: 'pendingReason', label: 'Pending Reason' },
  ],
};

/**
 * Phase 1 Payment History viewer.
 *
 * Design notes:
 * - Caps display at 10 latest rows (backend enforces it too). A footer
 *   badge shows "N of TOTAL" so admins know when older rows exist —
 *   Phase 2 will add real pagination / filters when there's something
 *   worth paginating.
 * - Card-per-row layout instead of a cramped table: the nested
 *   paymentDetails blob is much easier to scan this way, and it reads
 *   cleanly on wider screens.
 * - Three summary tiles at the top: total records, lifetime amount, and
 *   latest payment method. These survive being empty (show "—") so the
 *   layout doesn't collapse on brand-new companies.
 */
class PaymentHistoryDialog extends React.Component {
  state = {
    expanded: {},
    editingId: null, // paymentId currently in edit mode (null = view mode)
    editDraft: null, // { paymentMethod, paymentDetails, autoRenewal, amount, notes }
    saving: false,
    feedback: null, // { type: 'success' | 'error', message }
    isMobile: window.matchMedia('(max-width: 600px)').matches,

    // Phase 1 pre-payment workflow. When `recordingNextCycle` is true the
    // dialog renders an inline form (mirrors the edit form) at the top of
    // the card list. nextCycleDraft mirrors editDraft's shape so it can
    // share renderEditForm-style fields without duplicating layout.
    recordingNextCycle: false,
    nextCycleDraft: null,
  };

  _mql = window.matchMedia('(max-width: 600px)');
  _onMql = (e) => this.setState({ isMobile: e.matches });

  componentDidMount() {
    this._mql.addListener(this._onMql);
    if (this.props.open && this.props.companyId) {
      this.props.listLicensePayments({ companyId: this.props.companyId });
    }
  }

  componentWillUnmount() {
    this._mql.removeListener(this._onMql);
  }

  componentDidUpdate(prevProps) {
    const justOpened = this.props.open && !prevProps.open;
    const companyChanged =
      this.props.companyId && prevProps.companyId !== this.props.companyId;
    if (
      this.props.open &&
      this.props.companyId &&
      (justOpened || companyChanged)
    ) {
      this.props.listLicensePayments({ companyId: this.props.companyId });
      this.setState({
        expanded: {},
        editingId: null,
        editDraft: null,
        feedback: null,
        recordingNextCycle: false,
        nextCycleDraft: null,
      });
    }
  }

  // ---- edit-mode lifecycle ----------------------------------------------

  beginEdit = (payment) => {
    this.setState({
      editingId: payment.paymentId,
      editDraft: {
        paymentMethod: payment.paymentMethod || PAYMENT_METHODS.CASH_ZELLE,
        paymentDetails: { ...(payment.paymentDetails || {}) },
        autoRenewal: !!payment.autoRenewal,
        amount: payment.amount == null ? '' : String(payment.amount),
        notes: payment.notes || '',
      },
      // Auto-expand the card so the form is visible once edit mode starts.
      expanded: { ...this.state.expanded, [payment.paymentId]: true },
      feedback: null,
    });
  };

  cancelEdit = () => {
    this.setState({ editingId: null, editDraft: null });
  };

  setEditField = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    this.setState((prev) => ({
      editDraft: { ...prev.editDraft, [field]: value },
    }));
  };

  setEditDetail = (key) => (e) => {
    const value = e.target.value;
    this.setState((prev) => ({
      editDraft: {
        ...prev.editDraft,
        paymentDetails: { ...prev.editDraft.paymentDetails, [key]: value },
      },
    }));
  };

  setEditMethod = (e) => {
    const nextMethod = e.target.value;
    // Switching methods clears details so the form only shows fields
    // relevant to the new method — same behavior as wizard Step 4.
    this.setState((prev) => ({
      editDraft: {
        ...prev.editDraft,
        paymentMethod: nextMethod,
        paymentDetails: {},
      },
    }));
  };

  setEditAutoRenewal = (_e, checked) => {
    this.setState((prev) => ({
      editDraft: { ...prev.editDraft, autoRenewal: checked },
    }));
  };

  // One-click collection status change. Used by the "Mark Collected" /
  // "Mark Not Collected" / "Revert to Pending" buttons on Collect-Later
  // cards — avoids dropping into the full edit form just to flip a flag.
  setCollectionStatus = (payment, nextStatus) => {
    this.setState({ saving: true, feedback: null });
    this.props.updateLicensePayment(
      {
        paymentId: payment.paymentId,
        companyId: this.props.companyId,
        collectionStatus: nextStatus,
      },
      (success, message, extra) => {
        if (success) {
          const reactivated = extra && extra.reactivated;
          const baseLabel =
            nextStatus === COLLECTION_STATUS.COLLECTED
              ? 'Marked as collected'
              : nextStatus === COLLECTION_STATUS.UNCOLLECTIBLE
              ? 'Marked as not collected'
              : 'Reset to pending';
          const label = reactivated
            ? extra.reactivationMessage ||
              'Company reactivated — license is live again.'
            : baseLabel;
          this.setState({
            saving: false,
            feedback: { type: 'success', message: label },
          });
        } else {
          this.setState({
            saving: false,
            feedback: {
              type: 'error',
              message: message || 'Could not update collection status',
            },
          });
        }
      }
    );
  };

  // ---- next-cycle pre-payment lifecycle ---------------------------------

  beginNextCycle = () => {
    this.setState({
      recordingNextCycle: true,
      nextCycleDraft: {
        paymentMethod: PAYMENT_METHODS.CASH_ZELLE,
        paymentDetails: {},
        autoRenewal: true,
        amount: '',
        notes: '',
        // markPaid defaults true for non-pending methods so the most
        // common path (admin records a Zelle/cash receipt) is one click.
        // Switching to "Collect Later" flips it off in setNextCycleMethod.
        markPaid: true,
      },
      feedback: null,
    });
  };

  cancelNextCycle = () => {
    this.setState({ recordingNextCycle: false, nextCycleDraft: null });
  };

  setNextCycleField = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    this.setState((prev) => ({
      nextCycleDraft: { ...prev.nextCycleDraft, [field]: value },
    }));
  };

  setNextCycleDetail = (key) => (e) => {
    const value = e.target.value;
    this.setState((prev) => ({
      nextCycleDraft: {
        ...prev.nextCycleDraft,
        paymentDetails: { ...prev.nextCycleDraft.paymentDetails, [key]: value },
      },
    }));
  };

  setNextCycleMethod = (e) => {
    const nextMethod = e.target.value;
    this.setState((prev) => ({
      nextCycleDraft: {
        ...prev.nextCycleDraft,
        paymentMethod: nextMethod,
        paymentDetails: {},
        // Collect-Later is by definition not-yet-paid — force markPaid off
        // so the form's checkbox doesn't lie about what the row will look
        // like on submit.
        markPaid:
          nextMethod === PAYMENT_METHODS.PENDING
            ? false
            : prev.nextCycleDraft.markPaid,
      },
    }));
  };

  setNextCycleAutoRenewal = (_e, checked) => {
    this.setState((prev) => ({
      nextCycleDraft: { ...prev.nextCycleDraft, autoRenewal: checked },
    }));
  };

  setNextCycleMarkPaid = (_e, checked) => {
    this.setState((prev) => ({
      nextCycleDraft: { ...prev.nextCycleDraft, markPaid: checked },
    }));
  };

  submitNextCycle = () => {
    const draft = this.state.nextCycleDraft;
    if (!draft) return;
    this.setState({ saving: true, feedback: null });
    this.props.recordNextCyclePayment(
      {
        companyId: this.props.companyId,
        paymentMethod: draft.paymentMethod,
        paymentDetails: draft.paymentDetails,
        autoRenewal: draft.autoRenewal,
        amount: draft.amount,
        notes: draft.notes,
        markPaid: draft.markPaid,
      },
      (success, message, extra) => {
        if (success) {
          const reactivated = extra && extra.reactivated;
          const banner = reactivated
            ? extra.reactivationMessage ||
              'Company reactivated — license is live again.'
            : message || 'Next-cycle payment recorded';
          this.setState({
            saving: false,
            recordingNextCycle: false,
            nextCycleDraft: null,
            feedback: { type: 'success', message: banner },
          });
        } else {
          this.setState({
            saving: false,
            feedback: {
              type: 'error',
              message: message || 'Could not record next-cycle payment',
            },
          });
        }
      }
    );
  };

  saveEdit = (payment) => {
    const draft = this.state.editDraft;
    if (!draft) return;

    this.setState({ saving: true, feedback: null });

    this.props.updateLicensePayment(
      {
        paymentId: payment.paymentId,
        companyId: this.props.companyId,
        paymentMethod: draft.paymentMethod,
        paymentDetails: draft.paymentDetails,
        autoRenewal: draft.autoRenewal,
        amount: draft.amount,
        notes: draft.notes,
      },
      (success, message, extra) => {
        if (success) {
          const reactivated = extra && extra.reactivated;
          const banner = reactivated
            ? extra.reactivationMessage ||
              'Company reactivated — license is live again.'
            : message || 'Payment record updated';
          this.setState({
            saving: false,
            editingId: null,
            editDraft: null,
            feedback: { type: 'success', message: banner },
          });
        } else {
          this.setState({
            saving: false,
            feedback: {
              type: 'error',
              message: message || 'Could not update payment record',
            },
          });
        }
      }
    );
  };

  // ---- helpers -----------------------------------------------------------

  toNumber = (amount) => {
    if (amount == null || amount === '') return 0;
    const n = Number(amount);
    return Number.isNaN(n) ? 0 : n;
  };

  formatAmount = (amount) => {
    const n = this.toNumber(amount);
    if (amount == null || amount === '') return '—';
    if (n === 0) return 'Free';
    return `$${n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  formatCurrencyTotal = (payments) => {
    const total = (payments || []).reduce(
      (sum, p) => sum + this.toNumber(p.amount),
      0
    );
    return `$${total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  formatDate = (value) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return String(value);
    }
  };

  // Cycle label (e.g. "Jan 2026") — derived from coversCycleEndDate when
  // present. This is what the prominent date column shows now, so admins
  // can tell at a glance which month a payment row is FOR rather than
  // when it happened to be recorded (which can be months later for
  // back-paid rows). Falls back to receivedAt month for legacy rows
  // produced before coversCycleEndDate existed.
  formatCycleLabel = (payment) => {
    if (!payment) return '—';
    const raw = payment.coversCycleEndDate || payment.receivedAt;
    if (!raw) return '—';
    try {
      const d = new Date(raw);
      return d.toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return '—';
    }
  };

  formatTime = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  };

  formatMethod = (method) => {
    if (!method) return 'Not Set';
    return method
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  formatKey = (k) =>
    k
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();

  // The discount stamp the server writes on every priced cycle row
  // (CompanyService.discountStamp) — shown as one readable line instead of
  // four raw fractions.
  DISCOUNT_STAMP_KEYS = [
    'discountPct',
    'locationDiscountPct',
    'prepayDiscountPct',
    'activeLocations',
  ];

  discountSummary = (details) => {
    if (!details || details.discountPct == null) return null;
    const pct = (v) => Math.round(Number(v || 0) * 100);
    const parts = [];
    const location = pct(details.locationDiscountPct);
    const prepay = pct(details.prepayDiscountPct);
    if (location > 0) {
      parts.push(
        `${location}% multi-location (${details.activeLocations} locations)`
      );
    }
    if (prepay > 0) parts.push(`${prepay}% prepay`);
    return parts.length ? parts.join(' + ') : null;
  };

  methodIcon = (method) => {
    const m = (method || '').toString().toLowerCase();
    if (m.includes('card')) return <PaymentIcon style={{ fontSize: 18 }} />;
    if (m.includes('bank'))
      return <AccountBalanceIcon style={{ fontSize: 18 }} />;
    if (m.includes('pending'))
      return <HourglassEmptyIcon style={{ fontSize: 18 }} />;
    return <AttachMoneyIcon style={{ fontSize: 18 }} />;
  };

  toggleExpanded = (paymentId) => {
    this.setState((prev) => ({
      expanded: { ...prev.expanded, [paymentId]: !prev.expanded[paymentId] },
    }));
  };

  // Future-cycle helpers. A row is "upcoming" when its coversCycleEndDate
  // is after now — the renewal job will treat it as the next cycle's
  // payment when the 15th hits. The duplicate guard below blocks the
  // pre-payment button when an upcoming row already exists and is not
  // marked uncollectible (admins should edit it rather than pile up rows).
  isFutureCycle = (payment) => {
    if (!payment || !payment.coversCycleEndDate) return false;
    try {
      return new Date(payment.coversCycleEndDate).getTime() > Date.now();
    } catch (e) {
      return false;
    }
  };

  hasOpenFutureCycleRow = (payments) =>
    (payments || []).some(
      (p) =>
        this.isFutureCycle(p) &&
        p.collectionStatus !== COLLECTION_STATUS.UNCOLLECTIBLE
    );

  hasDetails = (details) =>
    details &&
    typeof details === 'object' &&
    (!!this.discountSummary(details) ||
      Object.entries(details).some(
        ([k, v]) =>
          v != null && v !== '' && !this.DISCOUNT_STAMP_KEYS.includes(k)
      ));

  renderCollectionChip = (status) => {
    if (!status) return null;
    const config = {
      [COLLECTION_STATUS.PENDING]: {
        label: 'Awaiting Collection',
        bg: '#fffbeb',
        color: '#b45309',
        icon: <PendingActionsIcon style={{ fontSize: 14 }} />,
      },
      [COLLECTION_STATUS.COLLECTED]: {
        label: 'Collected',
        bg: '#ecfdf5',
        color: '#047857',
        icon: <DoneAllIcon style={{ fontSize: 14 }} />,
      },
      [COLLECTION_STATUS.UNCOLLECTIBLE]: {
        label: 'Not Collected',
        bg: '#fef2f2',
        color: '#b91c1c',
        icon: <DoNotDisturbIcon style={{ fontSize: 14 }} />,
      },
    };
    const c = config[status];
    if (!c) return null;
    return (
      <Chip
        size="small"
        icon={c.icon}
        label={c.label}
        style={{
          backgroundColor: c.bg,
          color: c.color,
          fontWeight: 700,
          height: 22,
          border: `1px solid ${c.color}22`,
        }}
      />
    );
  };

  renderCollectionActions = (payment) => {
    if (!payment.collectionStatus) return null;
    const isEditing = this.state.editingId === payment.paymentId;
    if (isEditing) return null;

    const status = payment.collectionStatus;
    const disabled = this.state.saving;

    if (status === COLLECTION_STATUS.PENDING) {
      return (
        <Box display="flex" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() =>
              this.setCollectionStatus(payment, COLLECTION_STATUS.COLLECTED)
            }
            startIcon={<DoneAllIcon style={{ fontSize: 16 }} />}
            style={{
              textTransform: 'none',
              borderColor: '#059669',
              color: '#059669',
              fontWeight: 600,
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            Mark Collected
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() =>
              this.setCollectionStatus(payment, COLLECTION_STATUS.UNCOLLECTIBLE)
            }
            startIcon={<DoNotDisturbIcon style={{ fontSize: 16 }} />}
            style={{
              textTransform: 'none',
              borderColor: '#dc2626',
              color: '#dc2626',
              fontWeight: 600,
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            Not Collected
          </Button>
        </Box>
      );
    }

    // Already collected or uncollectible — show collected-by info + revert
    return (
      <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 8 }}>
        {payment.collectedByName && (
          <Typography variant="caption" style={{ color: '#6b7280' }}>
            {status === COLLECTION_STATUS.COLLECTED
              ? 'Collected'
              : 'Marked uncollectible'}{' '}
            by {payment.collectedByName}
            {payment.collectedAt
              ? ` on ${this.formatDate(payment.collectedAt)}`
              : ''}
          </Typography>
        )}
        <Button
          size="small"
          disabled={disabled}
          onClick={() =>
            this.setCollectionStatus(payment, COLLECTION_STATUS.PENDING)
          }
          startIcon={<ReplayIcon style={{ fontSize: 14 }} />}
          style={{
            textTransform: 'none',
            color: '#6b7280',
            fontWeight: 600,
            minWidth: 0,
            padding: '2px 8px',
          }}
        >
          Revert
        </Button>
      </Box>
    );
  };

  // ---- renderers ---------------------------------------------------------

  renderSummary = (payments, totalCount) => {
    const latest = payments && payments[0];
    const latestMethod = latest ? this.formatMethod(latest.paymentMethod) : '—';
    const tiles = [
      { label: 'Records', value: totalCount || 0, color: '#2563eb' },
      {
        label: 'Billed',
        value: this.formatCurrencyTotal(payments),
        color: '#059669',
      },
      { label: 'Method', value: latestMethod, color: '#7c3aed' },
    ];
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
          marginBottom: 14,
        }}
      >
        {tiles.map((t) => (
          <div
            key={t.label}
            style={{
              background: '#fff',
              border: '1px solid #edf0f5',
              borderRadius: 10,
              padding: '10px 12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: t.color,
                lineHeight: 1,
              }}
            >
              {t.value}
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#8a9cc5',
                letterSpacing: 0.8,
                marginTop: 4,
                textTransform: 'uppercase',
              }}
            >
              {t.label}
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderDetailRow = (label, value) => (
    <div
      key={label}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '5px 0',
        borderBottom: '1px solid #f3f4f6',
        fontSize: 14,
      }}
    >
      <span style={{ color: '#8a9cc5', fontWeight: 600 }}>{label}</span>
      <span
        style={{
          color: '#0f172a',
          fontWeight: 600,
          textAlign: 'right',
          wordBreak: 'break-word',
          maxWidth: '60%',
        }}
      >
        {value}
      </span>
    </div>
  );

  renderChip = (label, bg, color, icon) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 12,
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 4,
        background: bg,
        color,
        whiteSpace: 'nowrap',
        letterSpacing: 0.2,
      }}
    >
      {icon}
      {label}
    </span>
  );

  renderPaymentCard = (payment) => {
    const isEditing = this.state.editingId === payment.paymentId;
    const expanded = !!this.state.expanded[payment.paymentId] || isEditing;
    const hasDetails = this.hasDetails(payment.paymentDetails);
    const hasNotes = !!(
      payment.notes &&
      payment.notes.trim() &&
      !payment.notes.toLowerCase().includes('auto-generated')
    );
    const canExpand = hasDetails || hasNotes || payment.licenseKey || isEditing;

    const amount = this.formatAmount(payment.amount);
    const isFree = amount === 'Free';

    return (
      <div
        key={payment.paymentId}
        style={{
          background: '#fff',
          border: isEditing ? '1.5px solid #4338ca' : '1px solid #edf0f5',
          borderRadius: 10,
          padding: '10px 12px',
          marginBottom: 8,
          transition: 'border-color 120ms ease',
          boxShadow: isEditing
            ? '0 0 0 2px rgba(67,56,202,0.06)'
            : '0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {/* Top row: cycle month | plan | amount */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Cycle this payment covers (e.g. "Jan 2026"). receivedAt moved
              to the Recorded-by line below so back-paid rows show which
              month they pay FOR, not when the click happened. */}
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#0f172a',
              minWidth: 74,
            }}
            title="Cycle this payment covers"
          >
            {this.formatCycleLabel(payment)}
          </div>

          {/* Plan + chips */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#0f172a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {payment.planName || 'Unnamed plan'}
            </div>
          </div>

          {/* Amount */}
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              color: isFree ? '#16a34a' : '#0f172a',
              whiteSpace: 'nowrap',
            }}
          >
            {amount}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 0, marginLeft: 2 }}>
            {!isEditing && (
              <IconButton
                size="small"
                onClick={() => this.beginEdit(payment)}
                title="Edit"
                style={{ padding: 4 }}
              >
                <EditIcon style={{ fontSize: 14, color: '#8a9cc5' }} />
              </IconButton>
            )}
            {canExpand && !isEditing && (
              <IconButton
                size="small"
                onClick={() => this.toggleExpanded(payment.paymentId)}
                style={{ padding: 4 }}
              >
                {expanded ? (
                  <ExpandLessIcon style={{ fontSize: 16, color: '#8a9cc5' }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: 16, color: '#8a9cc5' }} />
                )}
              </IconButton>
            )}
          </div>
        </div>

        {/* Chips row */}
        <div
          style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}
        >
          {this.isFutureCycle(payment) &&
            this.renderChip(
              'Upcoming',
              '#dbeafe',
              '#1d4ed8',
              <EventAvailableIcon style={{ fontSize: 12, marginRight: 2 }} />
            )}
          {payment.duration &&
            this.renderChip(payment.duration, '#eef2ff', '#4338ca')}
          {this.renderChip(
            this.formatMethod(payment.paymentMethod),
            '#f1f5f9',
            '#475569'
          )}
          {this.renderChip(
            payment.autoRenewal ? 'Auto' : 'Manual',
            payment.autoRenewal ? '#ecfdf5' : '#fef2f2',
            payment.autoRenewal ? '#047857' : '#b91c1c',
            payment.autoRenewal ? (
              <AutorenewIcon style={{ fontSize: 12, marginRight: 2 }} />
            ) : null
          )}
          {payment.collectionStatus === 'pending' &&
            this.renderChip('Pending', '#fffbeb', '#b45309')}
          {payment.collectionStatus === 'collected' &&
            this.renderChip('Collected', '#ecfdf5', '#047857')}
          {payment.collectionStatus === 'uncollectible' &&
            this.renderChip('Not Collected', '#fef2f2', '#b91c1c')}
        </div>

        {/* Warning for missing method */}
        {!payment.paymentMethod && !isEditing && (
          <div
            onClick={() => this.beginEdit(payment)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 6,
              padding: '4px 8px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              color: '#92400e',
              fontWeight: 600,
            }}
          >
            <EditIcon style={{ fontSize: 12 }} /> No method — click to update
          </div>
        )}

        {/* Recorded by + receipt date — moved here so the primary date
            column above can show the cycle month instead. */}
        {(payment.recordedByName || payment.receivedAt) && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            {payment.recordedByName && `Recorded by ${payment.recordedByName}`}
            {payment.recordedByName && payment.receivedAt && ' · '}
            {payment.receivedAt && this.formatDate(payment.receivedAt)}
          </div>
        )}

        {/* Collection actions */}
        {this.renderCollectionActions(payment)}

        {/* Expandable details */}
        {canExpand && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid #f3f4f6',
              }}
            >
              {isEditing
                ? this.renderEditForm(payment)
                : this.renderReadOnlyDetails(payment, hasDetails, hasNotes)}
            </div>
          </Collapse>
        )}
      </div>
    );
  };

  renderReadOnlyDetails = (payment, hasDetails, hasNotes) => {
    return (
      <>
        {payment.licenseKey && (
          <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
            <VpnKeyIcon
              style={{ fontSize: 16, color: '#6b7280', marginRight: 6 }}
            />
            <Typography
              variant="caption"
              style={{
                color: '#6b7280',
                marginRight: 6,
                fontWeight: 600,
              }}
            >
              License Key
            </Typography>
            <code
              style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: '2px 8px',
                fontSize: 12,
                color: '#374151',
              }}
            >
              {payment.licenseKey}
            </code>
          </Box>
        )}

        {hasDetails && (
          <Box style={{ marginBottom: hasNotes ? 12 : 0 }}>
            <Typography
              variant="caption"
              style={{
                color: '#6b7280',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                marginBottom: 6,
              }}
            >
              Payment Details
            </Typography>
            {this.discountSummary(payment.paymentDetails) &&
              this.renderDetailRow(
                'Discounts',
                this.discountSummary(payment.paymentDetails)
              )}
            {Object.entries(payment.paymentDetails)
              .filter(
                ([k, v]) =>
                  v != null &&
                  v !== '' &&
                  k !== 'notes' &&
                  !this.DISCOUNT_STAMP_KEYS.includes(k)
              )
              .map(([k, v]) =>
                this.renderDetailRow(
                  this.formatKey(k),
                  typeof v === 'object' ? JSON.stringify(v) : String(v)
                )
              )}
          </Box>
        )}

        {hasNotes && (
          <Box>
            <Typography
              variant="caption"
              style={{
                color: '#6b7280',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                marginBottom: 4,
              }}
            >
              Notes
            </Typography>
            <Typography variant="body2" style={{ color: '#374151' }}>
              {payment.notes}
            </Typography>
          </Box>
        )}

        {payment.lastModifiedByName && (
          <Box
            display="flex"
            alignItems="center"
            style={{ marginTop: 10, color: '#9ca3af', gap: 4 }}
          >
            <HistoryIcon style={{ fontSize: 14 }} />
            <Typography variant="caption">
              Last edited by {payment.lastModifiedByName}
              {payment.lastModifiedAt
                ? ` • ${this.formatDate(
                    payment.lastModifiedAt
                  )} ${this.formatTime(payment.lastModifiedAt)}`
                : ''}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  renderEditForm = (payment) => {
    const draft = this.state.editDraft;
    if (!draft) return null;

    const fields = DETAIL_FIELDS_BY_METHOD[draft.paymentMethod] || [];
    const inputProps = { style: { fontSize: 14, padding: '8px 10px' } };
    const labelProps = { style: { fontSize: 13 } };

    return (
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginBottom: 8,
          }}
        >
          <EditIcon style={{ fontSize: 12, color: '#4338ca' }} />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#4338ca',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            Editing payment record
          </span>
        </div>

        {payment.licenseKey && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginBottom: 8,
              fontSize: 12,
            }}
          >
            <VpnKeyIcon style={{ fontSize: 12, color: '#8a9cc5' }} />
            <span style={{ color: '#8a9cc5', fontWeight: 600 }}>Key:</span>
            <code
              style={{
                background: '#f1f5f9',
                border: '1px solid #edf0f5',
                borderRadius: 4,
                padding: '1px 6px',
                fontSize: 12,
                color: '#374151',
              }}
            >
              {payment.licenseKey}
            </code>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <TextField
            select
            label="Method"
            value={draft.paymentMethod}
            onChange={this.setEditMethod}
            variant="outlined"
            size="small"
            InputProps={inputProps}
            InputLabelProps={labelProps}
          >
            {PAYMENT_METHOD_LIST.map((m) => (
              <MenuItem key={m.id} value={m.id} style={{ fontSize: 14 }}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount"
            value={draft.amount}
            onChange={this.setEditField('amount')}
            variant="outlined"
            size="small"
            placeholder="0.00"
            InputProps={{
              ...inputProps,
              startAdornment: (
                <span
                  style={{ color: '#8a9cc5', marginRight: 2, fontSize: 14 }}
                >
                  $
                </span>
              ),
            }}
            InputLabelProps={labelProps}
          />
        </div>

        {fields.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#8a9cc5',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              Payment Details
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: fields.length <= 2 ? '1fr 1fr' : '1fr 1fr',
                gap: 8,
              }}
            >
              {fields.map((f) => (
                <TextField
                  key={f.key}
                  label={f.label}
                  value={draft.paymentDetails[f.key] || ''}
                  onChange={this.setEditDetail(f.key)}
                  variant="outlined"
                  size="small"
                  InputProps={inputProps}
                  InputLabelProps={labelProps}
                />
              ))}
            </div>
          </div>
        )}

        {draft.paymentMethod !== PAYMENT_METHODS.PENDING && (
          <TextField
            label="Notes"
            value={draft.notes || ''}
            onChange={this.setEditField('notes')}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={1}
            style={{ marginBottom: 6 }}
            placeholder="Optional memo"
            InputProps={{ style: { fontSize: 14 } }}
            InputLabelProps={labelProps}
          />
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 4,
          }}
        >
          <FormControlLabel
            style={{ marginLeft: 0 }}
            control={
              <Switch
                checked={!!draft.autoRenewal}
                onChange={this.setEditAutoRenewal}
                color="primary"
                size="small"
              />
            }
            label={
              <span style={{ fontSize: 14, color: '#374151' }}>
                Auto-renewal
              </span>
            }
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <Button
              onClick={this.cancelEdit}
              size="small"
              disabled={this.state.saving}
              style={{
                fontSize: 13,
                textTransform: 'none',
                minWidth: 0,
                padding: '4px 12px',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => this.saveEdit(payment)}
              size="small"
              variant="contained"
              color="primary"
              disabled={this.state.saving}
              style={{
                fontSize: 13,
                textTransform: 'none',
                minWidth: 0,
                padding: '4px 14px',
                borderRadius: 6,
              }}
              startIcon={
                this.state.saving ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SaveIcon style={{ fontSize: 14 }} />
                )
              }
            >
              {this.state.saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNextCycleForm = () => {
    const draft = this.state.nextCycleDraft;
    if (!draft) return null;

    const fields = DETAIL_FIELDS_BY_METHOD[draft.paymentMethod] || [];
    const inputProps = { style: { fontSize: 14, padding: '8px 10px' } };
    const labelProps = { style: { fontSize: 13 } };
    const isPendingMethod = draft.paymentMethod === PAYMENT_METHODS.PENDING;

    return (
      <div
        style={{
          background: '#fff',
          border: '1.5px solid #4338ca',
          borderRadius: 10,
          padding: '12px 12px',
          marginBottom: 10,
          boxShadow: '0 0 0 2px rgba(67,56,202,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 10,
          }}
        >
          <EventAvailableIcon style={{ fontSize: 14, color: '#4338ca' }} />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#4338ca',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            Record next-cycle payment
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <TextField
            select
            label="Method"
            value={draft.paymentMethod}
            onChange={this.setNextCycleMethod}
            variant="outlined"
            size="small"
            InputProps={inputProps}
            InputLabelProps={labelProps}
          >
            {PAYMENT_METHOD_LIST.map((m) => (
              <MenuItem key={m.id} value={m.id} style={{ fontSize: 14 }}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount"
            value={draft.amount}
            onChange={this.setNextCycleField('amount')}
            variant="outlined"
            size="small"
            placeholder="0.00"
            InputProps={{
              ...inputProps,
              startAdornment: (
                <span
                  style={{ color: '#8a9cc5', marginRight: 2, fontSize: 14 }}
                >
                  $
                </span>
              ),
            }}
            InputLabelProps={labelProps}
          />
        </div>

        {fields.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#8a9cc5',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              Payment Details
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {fields.map((f) => (
                <TextField
                  key={f.key}
                  label={f.label}
                  value={draft.paymentDetails[f.key] || ''}
                  onChange={this.setNextCycleDetail(f.key)}
                  variant="outlined"
                  size="small"
                  InputProps={inputProps}
                  InputLabelProps={labelProps}
                />
              ))}
            </div>
          </div>
        )}

        {!isPendingMethod && (
          <TextField
            label="Notes"
            value={draft.notes || ''}
            onChange={this.setNextCycleField('notes')}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={1}
            style={{ marginBottom: 6 }}
            placeholder="Optional memo"
            InputProps={{ style: { fontSize: 14 } }}
            InputLabelProps={labelProps}
          />
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 4,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 12, flexWrap: 'wrap' }}
          >
            <FormControlLabel
              style={{ marginLeft: 0 }}
              control={
                <Switch
                  checked={!!draft.autoRenewal}
                  onChange={this.setNextCycleAutoRenewal}
                  color="primary"
                  size="small"
                />
              }
              label={
                <span style={{ fontSize: 14, color: '#374151' }}>
                  Auto-renewal
                </span>
              }
            />
            <FormControlLabel
              style={{ marginLeft: 0 }}
              control={
                <Switch
                  checked={!isPendingMethod && !!draft.markPaid}
                  onChange={this.setNextCycleMarkPaid}
                  disabled={isPendingMethod}
                  color="primary"
                  size="small"
                />
              }
              label={
                <span style={{ fontSize: 14, color: '#374151' }}>
                  Mark as paid
                </span>
              }
            />
          </Box>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button
              onClick={this.cancelNextCycle}
              size="small"
              disabled={this.state.saving}
              style={{
                fontSize: 13,
                textTransform: 'none',
                minWidth: 0,
                padding: '4px 12px',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.submitNextCycle}
              size="small"
              variant="contained"
              color="primary"
              disabled={this.state.saving}
              style={{
                fontSize: 13,
                textTransform: 'none',
                minWidth: 0,
                padding: '4px 14px',
                borderRadius: 6,
              }}
              startIcon={
                this.state.saving ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SaveIcon style={{ fontSize: 14 }} />
                )
              }
            >
              {this.state.saving ? 'Saving...' : 'Save payment'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNextCycleBanner = (payments) => {
    const blocked = this.hasOpenFutureCycleRow(payments);
    if (this.state.recordingNextCycle) return this.renderNextCycleForm();

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '10px 12px',
          marginBottom: 10,
          background: blocked ? '#f9fafb' : '#eef2ff',
          border: `1px dashed ${blocked ? '#e5e7eb' : '#c7d2fe'}`,
          borderRadius: 10,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
        >
          <EventAvailableIcon
            style={{
              fontSize: 18,
              color: blocked ? '#9ca3af' : '#4338ca',
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              {blocked
                ? 'Next cycle already pre-recorded'
                : 'Take payment for next cycle'}
            </div>
            <div
              style={{
                fontSize: 11,
                color: '#6b7280',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {blocked
                ? 'Edit the upcoming row below if details need to change.'
                : 'Record payment before the 15th so the license auto-renews.'}
            </div>
          </div>
        </div>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={blocked}
          onClick={this.beginNextCycle}
          startIcon={<AddCircleOutlineIcon style={{ fontSize: 14 }} />}
          style={{
            fontSize: 12,
            textTransform: 'none',
            borderRadius: 6,
            padding: '4px 12px',
            whiteSpace: 'nowrap',
          }}
        >
          Pre-Record
        </Button>
      </div>
    );
  };

  renderEmpty = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        background: '#fff',
        border: '1px dashed #edf0f5',
        borderRadius: 10,
      }}
    >
      <ReceiptLongIcon style={{ fontSize: 36, color: '#d1d5db' }} />
      <div
        style={{
          marginTop: 8,
          fontWeight: 600,
          color: '#374151',
          fontSize: 15,
        }}
      >
        No payment records yet
      </div>
      <div
        style={{
          color: '#9ca3af',
          marginTop: 4,
          fontSize: 12,
          textAlign: 'center',
          maxWidth: 280,
        }}
      >
        A record is created automatically when a license is issued or renewed.
      </div>
    </div>
  );

  render() {
    const { open, onClose, payments, totalCount, companyName } = this.props;
    const rows = payments || [];
    const visible = rows.slice(0, 10);

    return (
      <Dialog
        open={!!open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={this.state.isMobile}
        PaperProps={{
          style: {
            borderRadius: this.state.isMobile ? 0 : 16,
            background: '#f9fafb',
          },
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            background: '#fff',
            borderBottom: '1px solid #edf0f5',
            padding: '12px 16px',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: '#eef2ff',
                  color: '#4338ca',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLongIcon style={{ fontSize: 16 }} />
              </Box>
              <Box>
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: '#0f172a',
                    lineHeight: 1.2,
                  }}
                >
                  Payment History
                </Typography>
                {companyName && (
                  <Typography
                    style={{ color: '#8a9cc5', fontSize: 11, marginTop: 1 }}
                  >
                    {companyName}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers={false}
          style={{ padding: '12px 14px', background: '#f4f5f8' }}
        >
          {this.state.feedback && (
            <Box
              display="flex"
              alignItems="center"
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                marginBottom: 12,
                background:
                  this.state.feedback.type === 'success'
                    ? '#ecfdf5'
                    : '#fef2f2',
                border: `1px solid ${
                  this.state.feedback.type === 'success' ? '#a7f3d0' : '#fecaca'
                }`,
                color:
                  this.state.feedback.type === 'success'
                    ? '#047857'
                    : '#b91c1c',
              }}
            >
              {this.state.feedback.type === 'success' ? (
                <CheckCircleIcon style={{ fontSize: 18, marginRight: 8 }} />
              ) : (
                <CancelIcon style={{ fontSize: 18, marginRight: 8 }} />
              )}
              <span style={{ flex: 1, fontWeight: 600, fontSize: 11 }}>
                {this.state.feedback.message}
              </span>
              <IconButton
                size="small"
                onClick={() => this.setState({ feedback: null })}
                style={{ color: 'inherit' }}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {this.renderSummary(rows, totalCount)}

          {this.renderNextCycleBanner(rows)}

          {rows.length === 0 ? (
            this.renderEmpty()
          ) : (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ marginBottom: 8 }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#8a9cc5',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                  }}
                >
                  Recent Activity
                </span>
                <span
                  style={{ fontSize: 11, fontWeight: 600, color: '#b0b8cc' }}
                >
                  {visible.length} of {totalCount || visible.length}
                </span>
              </Box>

              <Divider style={{ marginBottom: 12 }} />

              {visible.map((p) => this.renderPaymentCard(p))}

              {totalCount > visible.length && (
                <Box
                  textAlign="center"
                  py={1}
                  style={{
                    color: '#b0b8cc',
                    fontSize: 10,
                    fontStyle: 'italic',
                  }}
                >
                  {totalCount - visible.length} older record
                  {totalCount - visible.length === 1 ? '' : 's'} not shown
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          style={{
            background: '#fff',
            borderTop: '1px solid #edf0f5',
            padding: '8px 16px',
          }}
        >
          <Button
            onClick={onClose}
            size="small"
            variant="contained"
            color="primary"
            style={{ fontSize: 11, textTransform: 'none', borderRadius: 6 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapStateToProps = (state) => ({
  payments: licensePaymentsSelector(state),
  totalCount: licensePaymentsTotalSelector(state),
});

const actionCreators = {
  listLicensePayments,
  updateLicensePayment,
  recordNextCyclePayment,
};

export default withShipment(
  { mapStateToProps, actionCreators },
  PaymentHistoryDialog
);
